import React, { useEffect, useRef, useState } from "react";
import Plot from "react-plotly.js";
import scatterData from "../../datas/scatter2dData.json";
import { Box, Button } from "@mui/material";
import Plotly, { update } from "plotly.js";
import { getProject } from "../../services/projectService";
import { useRecoilState, useRecoilValue } from "recoil";
import { selectedAlgoState } from "../../recoil";
import { t } from "i18next";
import { COLORS } from "../../styles/GlobalStyles";

interface ScatterData {
  x: number;
  y: number;
}

interface GroupedData {
  [key: string]: ScatterData[];
}

const ScatterPlot = ({
  data,
  groupedData,
  classList,
}: {
  data: Array<any>;
  groupedData: GroupedData;
  classList: string[];
}) => {
  const traces = classList.map((className, index) => {
    const classPoints = data[0].x
      .map((x: number, i: number) => ({
        x: x,
        y: data[0].y[i],
        label: data[0].marker.color[i] === COLORS[index] ? className : null,
      }))
      .filter((point: any) => point.label === className);

    return {
      x: classPoints.map((p: any) => p.x),
      y: classPoints.map((p: any) => p.y),
      imageId: classPoints.map((p: any) => p.id),
      text: classPoints.map((p: any) => p.path),
      mode: "markers",
      name: className,
      marker: {
        size: 10,
        color: COLORS[index],
      },
      type: "scatter",
    };
  });

  return (
    <Plot
      data={traces as any}
      layout={{
        margin: { l: 0, r: 0, t: 0, b: 0 },
        showlegend: true,
        width: 270,
        xaxis: {
          fixedrange: true,
        },
        yaxis: {
          fixedrange: true,
        },
      }}
      config={{ displayModeBar: false }}
    />
  );
};

interface PreClassChartProps {
  algoItem: any;
  images: Array<any>;
  classList: string[];
  setCurrentStep: (step: number) => void;
}

const PreClassChart = ({
  algoItem,
  images,
  classList,
  setCurrentStep,
}: PreClassChartProps) => {
  const COLORS = [
    "red",
    "blue",
    "green",
    "orange",
    "purple",
    "pink",
    "gray",
    "brown",
    "cyan",
  ];

  const [selectAlgorithm, setSelectAlgorithm] =
    useRecoilState(selectedAlgoState);
  const [groupedData, setGroupedData] = useState<GroupedData>({
    unlabeled: scatterData.unlabeled,
    firstG: [],
    secondG: [],
    thirdG: [],
  });
  const [updateImages, setUpdateImages] = useState<any[]>([]);
  const [labels, setLabels] = useState<string[]>([]);

  useEffect(() => {
    setLabels(classList);
    const updatedImages = addRandomCoordinates(images).map((img) => {
      const randomLabel =
        classList[Math.floor(Math.random() * classList.length)];
      return {
        ...img,
        AILabel: randomLabel,
        recentLabel: randomLabel,
      };
    });

    setUpdateImages(updatedImages);
  }, []);

  // 랜덤 숫자를 생성하는 함수
  const getRandomValue = (min: number, max: number): number => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  // images 배열에 x와 y 값을 추가하는 함수
  const addRandomCoordinates = (images: Array<any>): Array<any> => {
    return images.map((image) => ({
      ...image,
      x: getRandomValue(-10, 10),
      y: getRandomValue(-10, 10),
    }));
  };

  const scatterDatas = [
    {
      x: updateImages.map((img) => img.x), // x 좌표 배열
      y: updateImages.map((img) => img.y), // y 좌표 배열
      imageId: updateImages.map((img) => img.id),
      text: updateImages.map((img) => img.path), // 이미지 경로를 hover로 표시
      mode: "markers",
      marker: {
        size: 10,
        color: updateImages.map((img) => {
          const labelIndex = labels.indexOf(img.recentLabel);
          return labelIndex !== -1
            ? COLORS[labelIndex % COLORS.length]
            : "black";
        }),
      },
    },
  ];

  const handleSelectAlgorithm = () => {
    console.log(updateImages);
    setCurrentStep(3);
    setSelectAlgorithm({
      ...selectAlgorithm,
      algoId: algoItem.id,
      algoName: algoItem.name,
      images: updateImages,
      labels: labels,
    });
  };

  return (
    <Box sx={{ width: "fit-content", height: "500px" }}>
      <ScatterPlot
        data={scatterDatas}
        groupedData={groupedData}
        classList={labels}
      />
      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={handleSelectAlgorithm}>
          {t("Select")}
        </Button>
      </Box>
    </Box>
  );
};

export default PreClassChart;
